/* eslint-disable  no-bitwise */
/* eslint-disable  no-new */
import Cookies from 'js-cookie';
import dateFormat from 'dateformat';
import CryptoJS from 'crypto-js';
import { interval } from 'rxjs';
import { getUserState } from '@carvana/core-auth';
import { Buckets, DateMasks, Months, Days, VerificationString } from '../constants';
import { currentAssetsUrl } from '../utils/baseUrlUtil';

/* 

  This service is for generic logic, the rule of thumb is -
  if the code can be copy/pasted and used in any application
  with no changes  

 */

export const formatCurrency = (amount: number, maximumFractionDigits?: number): string => {
  const dollar = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits,
  });
  return dollar.format(amount);
};

export const subtractCurrency = (amount: number, minusAmount: number, maximumFractionDigits?: number): string => {
  const dollar = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits,
  });
  return dollar.format(amount - minusAmount);
};

export const getDateWithTimeZoneOffset = (date: string | number) => {
  new Date(date);
};

export const getFormattedDate = (date: string, isDayVisible = true) => {
  const dateObj = new Date(date.split('T')[0]);
  const monthNames = [
    Months.JAN,
    Months.FEB,
    Months.MAR,
    Months.APR,
    Months.MAY,
    Months.JUN,
    Months.JUL,
    Months.AUG,
    Months.SEP,
    Months.OCT,
    Months.NOV,
    Months.DEC,
  ];
  const dayNames = [Days.SUN, Days.MON, Days.TUE, Days.WED, Days.THU, Days.FRI, Days.SAT];
  const dayName = isDayVisible ? `${dayNames[dateObj.getUTCDay()]}, ` : '';
  return `${dayName}${monthNames[dateObj.getUTCMonth()]} ${dateObj.getUTCDate()}, ${dateObj.getUTCFullYear()}`;
};

export const getDaysPastDate = (then: any, now: any) => {
  const date1 = new Date(then);
  date1.setHours(0, 0, 0, 0);
  const date2 = new Date(now);
  date2.setHours(0, 0, 0, 0);
  const diffInTiume = date1.getTime() - date2.getTime();
  const diffInDays = Math.abs(diffInTiume / (1000 * 3600 * 24));
  return Math.trunc(diffInDays);
};

export const getDifferenceInDollars = (oldNumber: number, newNumber: number) => {
  const difference = newNumber - oldNumber;
  return difference;
};

export const getExperimentValue = async (experimentName: string) => {
  return new Promise((resolve) => {
    const counter = interval(500);
    const subscription = counter.subscribe((val) => {
      const expValue = Cookies.get(experimentName);
      if (expValue !== Buckets.UNDEFINED) {
        subscription.unsubscribe();
        resolve(expValue);
      }
      if (val === 5) {
        subscription.unsubscribe();
        resolve(expValue);
      }
    });
  });
};

export const getDifferenceInPercent = (oldNumber: any, newNumber: any) => {
  const currencyDifference = Math.abs(
    // force it to always be a positive value
    convertFloatToInt(oldNumber) - convertFloatToInt(newNumber)
  );
  const percentChange = (currencyDifference / oldNumber) * 100;
  const changeDirection = determinePosOrNegDiff(oldNumber, newNumber);
  const percentDifference = changeDirection === '-' ? percentChange * -1 : percentChange;
  return parseFloat(percentDifference.toFixed(2));
};

export const determinePosOrNegDiff = (oldNum: any, newNum: any) => {
  return oldNum - newNum > 0 ? '-' : '+';
};

export const convertFloatToInt = (value: any) => {
  return value | 0;
};

export const getLocalStorage = (key: string) => {
  return localStorage.getItem(key) ?? false;
};

export const setLocalStorage = (key: string, value: string) => {
  localStorage.setItem(key, value);
  return Boolean(value);
};

export const getSessionStorageObject = (key: string) => {
  const userId = getUserState().user?.user_id;
  let item: any = sessionStorage.getItem(key);
  if (item && userId) {
    try {
      const decrypted = CryptoJS.AES.decrypt(item, userId);
      item = JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));
    } catch (e) {
      item = {};
    }
  }
  if (!item) {
    item = {};
  }
  return item;
};

export const setSessionStorageObject = (key: string, value: object) => {
  const userId = getUserState().user?.user_id;
  const stringified = JSON.stringify(value);
  try {
    const encrypted = CryptoJS.AES.encrypt(stringified, userId).toString();
    sessionStorage.setItem(key, encrypted);
  } catch (e) {
    // TODO: Failing when token expires because userId is undefined.
    // console.log('encrypt error', userId);
  }
};

export const removeSessionStorage = (key: string) => {
  sessionStorage.removeItem(key);
};

export const getPageNavigationType = () => {
  const userId = getUserState().user?.user_id;
  if (!userId) {
    return 'signed-out';
  }
  const perfEntries = performance.getEntriesByType('navigation');
  const { type } = perfEntries[0].toJSON();
  return type;
};

export const formatDate = (date: any, mask = DateMasks.Default, showUTC = true) => {
  return dateFormat(date, mask, showUTC);
};

export const formatDateMST = (date: string | Date) => {
  // Create date object from input
  const utcDate = new Date(date);

  // Get UTC timestamp and adjust to MST (UTC-7)
  // Arizona is always UTC-7 (doesn't observe DST)
  const mstTime = utcDate.getTime() - 7 * 60 * 60 * 1000;
  const mstDate = new Date(mstTime);

  // Format as mm/dd/yyyy using UTC methods to avoid timezone interference
  const month = (mstDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = mstDate.getUTCDate().toString().padStart(2, '0');
  const year = mstDate.getUTCFullYear();

  return `${month}/${day}/${year}`;
};

export const randomSort = () => {
  // Get a random number between 0 and 10
  const { crypto }: any = window;
  const array = new Uint32Array(1);
  const temp = crypto.getRandomValues(array) * 10;
  // Get 1 or 0, whether temp is odd or even
  const isOddOrEven = temp % 2;
  // Get +1 or -1, whether temp greater or smaller than 5
  const isPosOrNeg = temp > 5 ? 1 : -1;
  // Return -1, 0, or +1
  return isOddOrEven * isPosOrNeg;
};

/* Handler for default image */
export const defaultImageHandler = (event: any) => {
  const defaultImageURL = `${currentAssetsUrl(process.env.NODE_ENV)}/op-assets/my-carvana/img_car_default.svg`;
  // To prevent infinite loop incase the defaultImageURL fails too
  if (event.target.src !== defaultImageURL) {
    event.target.src = defaultImageURL;
  }
};

// TODO - this logic is not generic needs a new home
export const getVerificationString = (verificationCount: number) => {
  return `${verificationCount} ${verificationCount > 1 ? VerificationString.PLURAL : VerificationString.SINGULAR}`;
};

export const checkOneYearHasPassed = (then: string | number | Date) => {
  const nowDate = new Date(Date.now());
  const thenDate = new Date(then);
  const timeDifference = Math.abs(nowDate.getTime() - thenDate.getTime());
  const dayDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
  return dayDifference >= 365;
};
